import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  buildRole11FlatKey,
  getRole11Schema,
  getRole11ValidatingControls,
  isRole11CalculatedControl,
  isRole11Empty,
  isRole11StaticallyRequired,
  resolveRole11ColumnSuffixes,
  resolveRole11LayoutFlags,
  Role11Schema,
} from './role11-sub-classification-liquidity.rules';

/**
 * Pure validator for Role 11 (Sub-classification of Assets, Liabilities &
 * Equity, by order of LIQUIDITY).
 *
 * Mirrors the editor's role11-220100-sub-classification-liquidity.component.ts
 * sheet-status surface. The component does NOT call
 * `updateSheetLevelStatus('subClassificationLiquidity', ...)` anywhere:
 * the only sheet-status writes are
 * `markSheetInitializedWithStatus('subClassificationLiquidity', false)` at
 * mount and after `loadSavedState()` settles. There is no
 * `evaluateSheetStatus()` loop and no `Validators.required` is wired in
 * `buildForm()` because the schema declares `required: false` on every
 * monetary cell and provides no `validators` array entries.
 *
 * Net effect: the editor's published `hasErrors` is always `false` for
 * this sheet today. The pure validator returns `hasErrors: false`
 * deterministically while carrying the LB-001 fix-guard pattern (required
 * detection from BOTH `control.required === true` AND unconditional
 * `validators: [{ name: 'required' }]`) so any future schema edit that
 * adds a required cell gets picked up automatically — matching roles
 * 22 / 21 / 13 / 9 (LB-009 / LB-010 / LB-011 / LB-012 / LB-013).
 *
 * ---- Calculations --------------------------------------------------------
 *
 * Role 11's component performs 10 row-sum totals per visible column suffix
 * (see `ROLE11_CALCULATION_GROUPS` in the rules file). The pure validator
 * does NOT need to replay them: the totals are persisted into the same
 * `subClassificationLiquidity` payload via `setValue(..., { emitEvent:
 * false })` and read directly out of `sheetData`. Since the schema
 * declares no `validation.equality` blocks and no `required: true` on any
 * cell, the calculated values do not influence the validation outcome
 * today. The row-group table is exported from the rules file so a future
 * equality block can plug in without re-deriving the column expansion.
 *
 * ---- Cross-sheet posture (Tier 4) ---------------------------------------
 *
 * Role 11 is registered as Tier 4 because it sits in the same
 * dependency-graph position as role 9 (sub-classification ↔ SOFP). The
 * orchestrator's SheetDependencyService lists `scopeOfFiling` as a
 * prerequisite of `subClassificationLiquidity` (column-visibility driver)
 * but does NOT wire a reverse edge from
 * `statementOfFinancialPositionLiquidity` (role 10) because the role 11
 * schema declares ZERO `validation.equality` blocks today — role 10
 * totals are never compared against role 11 subtotals.
 *
 * The validator still reads
 * `context.crossSheetData?.get('statementOfFinancialPositionLiquidity')`
 * defensively (per the Tier 4 task contract) so a future equality edit
 * can plug in without re-threading the interface; today the slice is
 * intentionally unused. Both `scopeOfFiling` and the role-10 slice may
 * be absent (parity-side mid-init, listing/dashboard cache-miss path) —
 * the validator tolerates absence by defaulting to the conservative
 * "single Current column" layout, matching the editor's pre-mount
 * initialiser values.
 */

const _UNUSED_FUTURE_CROSS_SHEET_KEY = SHEET_KEYS.statementOfFinancialPositionLiquidity;

/**
 * Mirror the component's row-level "required field with no value -> Failed"
 * branch, structurally identical to role 13's `checkRequiredRowErrors`.
 * Today this loop is a no-op against the live schema (zero required
 * controls) but the LB-001 fix-guard ensures any future schema edit gets
 * picked up automatically.
 *
 * Calculated / readonly rows are skipped — their values are produced by
 * the component's `calculateTotals()` after valueChanges settle, and the
 * editor never marks them required. If a future schema edit ever sets
 * `required: true` on a calculated row, this skip preserves the
 * behaviour the component would exhibit (the row would still receive a
 * value via setValue, so flagging it as "empty" would be misleading).
 */
function checkRequiredRowErrors(
  schema: Role11Schema,
  sheetData: Record<string, any>,
  context: SheetValidationContext
): Record<string, string> {
  const errors: Record<string, string> = {};

  const scopeSlice = context.crossSheetData?.get('scopeOfFiling');
  const { isConsolidated, hasPreviousYear } =
    resolveRole11LayoutFlags(scopeSlice);
  const suffixes = resolveRole11ColumnSuffixes(isConsolidated, hasPreviousYear);

  for (const control of getRole11ValidatingControls(schema)) {
    if (!isRole11StaticallyRequired(control)) continue;
    if (isRole11CalculatedControl(control)) continue;

    if (control.ui?.dualColumn) {
      // Mirror getRowValidationStatus(): a row is "Failed" only when ALL
      // visible columns are empty. If any column has a value, the row is
      // considered satisfied.
      const keys = suffixes.map((suffix) => buildRole11FlatKey(control.qname, suffix));
      const anyFilled = keys.some((key) => !isRole11Empty(sheetData[key]));
      if (!anyFilled) {
        errors[control.qname] = `${control.qname} is required`;
      }
    } else {
      // Single-control rows (e.g. the explanatory textarea) live under
      // the bare qname.
      if (isRole11Empty(sheetData[control.qname])) {
        errors[control.qname] = `${control.qname} is required`;
      }
    }
  }

  return errors;
}

export const role11SubClassificationLiquidityValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.subClassificationLiquidity,
  // Tier 4 per phase-2 plan: role 11 sits in the same dependency-graph
  // position as role 9 (sub-classification ↔ SOFP), with the calculation
  // surface to match. The validator's output is deterministic on
  // sheetData + scopeOfFiling alone today (no equality blocks, no
  // required cells), but the Tier 4 classification stays so the parity
  // harness exercises both paths and a future schema edit that adds a
  // genuine equality check against role 10 plugs in without re-tiering.
  tier: 4,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    // Cross-sheet slice is accessed defensively. Today no role 11
    // validation rule consumes it; the lookup exists so a future
    // `validation.equality` block referencing role 10 can plug in
    // without re-threading the interface. The orchestrator may pass an
    // empty Map or omit the key entirely — both cases resolve to
    // undefined here.
    const _role10Slice = context.crossSheetData?.get(
      _UNUSED_FUTURE_CROSS_SHEET_KEY
    );
    void _role10Slice;

    // Developer Note (TODO): Role-9-inheritance parity gap — source side.
    // Role 11 is the by-liquidity analog of Role 9: it is the SOURCE whose
    // totals are inherited into Role 10 (SOFP liquidity) leaves at mount time.
    // No fix is needed in THIS validator, but when Role 10 / Role 11 are
    // activated (they are dormant today, like Role 14) Role 10's validator
    // must replay this sheet's inheritance before its equality recompute — see
    // the matching Developer Note in role10-sofp-liquidity.validator.ts and the
    // shared `applyLinkedInheritance` helper used by the Role 8 / Role 12 fix.
    const schema = getRole11Schema();

    const errors: Record<string, string> = {
      ...checkRequiredRowErrors(schema, sheetData, context),
    };

    return {
      hasErrors: Object.keys(errors).length > 0,
      // Developer Note (TODO): stub — `hasSuggestedChanges` is hard-coded false,
      // so this sheet's dropdown "*" can never light. When released as a live
      // monetary sheet, mirror role13: (1) recompute via
      // CalculationService.calculateTotals_<id> + hasCalculatedFieldMismatch(
      // context.extractedBaseline ?? sheetData, recomputed); (2) add its sheet key
      // to PURE_VERDICT_SHEETS; (3) call evaluateSheetStatus() in the component's
      // runAllValidations(); (4) add its sheet key to CALC_DIFF_SHEETS in
      // suggestion-capability.ts, or the "(*) Optional: Suggestions to refine
      // data" legend will stay hidden on this sheet even once its "*" can light.
      hasSuggestedChanges: false,
      errors,
    };
  },
};
